<script setup lang="ts">
import type { ButtonGroupItem } from '@/types/button-group';
import type { EventInfo } from '@/types/event';

const props = defineProps({
	userId: {
		type: Number as PropType<number | null>,
		required: true,
	},
	groupId: {
		type: Number as PropType<number | null>,
		required: true,
	},
	isOwner: {
		type: Boolean,
		default: false,
	},
	isLoading: {
		type: Boolean,
		default: false,
	},
});

const emit = defineEmits<{
	(e: 'onUpdate'): void
}>();

const userStore = useUserStore();
const { isMobile } = useWindowResize();

const filterItem = ref<ButtonGroupItem[]>([
	{
		name: 'Upcoming',
		value: USER_EVENT_HOSTING_FILTER.UPCOMING,
	},
	{
		name: 'Archived',
		value: USER_EVENT_HOSTING_FILTER.ARCHIVED,
	},
]);

const selectedFilter = ref<string>(filterItem.value[0].value);
const currentPage = ref(1);
const items = ref<EventInfo[]>([]);

const profileId = computed(() => props.userId);
const groupId = computed(() => props.groupId);
const totalPages = computed(() => userStore.userEventHosting.singleResponse?.paginationInfo.totalPages);
const isLoading = computed(() => userStore.userEventHosting.isLoading || props.isLoading);

async function fetchData() {
	if (profileId.value && groupId.value) {
		await userStore.getUserHostingEventById(
			profileId.value,
			{
				itemPerPage: PAGINATION_CONFIG.ITEM_PER_PAGE,
				currentPage: currentPage.value,
				filter: selectedFilter.value,
				sortBy: USER_EVENT_SORTING.SORT_DESC,
				groupId: groupId.value,
			},
			true,
		);
		emit('onUpdate');
	}
}

function handleFilterChange() {
	currentPage.value = 1;
	items.value = [];
	fetchData();
}

function handlePageChange(page: number) {
	currentPage.value = page;
	fetchData();
}

watch(() => groupId.value, (newGroupId) => {
	if (newGroupId) {
		currentPage.value = 1;
		items.value = [];
		fetchData();
	}
});

watch(() => userStore.userEventHostingList, (newUserEventHostingList) => {
	items.value = newUserEventHostingList;
});

watch(
	() => [props.isOwner, profileId.value],
	([newIsOwner, newProfileId]) => {
		if (newIsOwner) {
			filterItem.value = [
				{
					name: 'Active',
					value: USER_EVENT_HOSTING_FILTER.ACTIVE,
				},
				{
					name: 'Archived',
					value: USER_EVENT_HOSTING_FILTER.ARCHIVED,
				},
			];
			selectedFilter.value = filterItem.value[0].value;
		}

		// Ensure fetchData() always has a valid profileId
		if (newProfileId) {
			fetchData();
		}
	},
	{ immediate: true },
);
</script>

<template>
  <div
    id="hosting-list"
    class="hosting-list"
  >
    <div class="header">
      <h1 class="text-lg text-semibold">
        Events
      </h1>
      <BaseToggleButtonGroup
        v-model="selectedFilter"
        :items="filterItem"
        @update:model-value="handleFilterChange"
      />
    </div>
    <template v-if="items?.length">
      <div class="items">
        <EventDetailsItem
          v-for="item in items"
          :key="item.id"
          :data="item"
          :is-hosting-user-profile="isOwner"
        >
          <template #action>
            <EventStatusTag
              class="status"
              :status="item.status"
              :size="isMobile ? TagSizes[0] : TagSizes[3]"
            />
          </template>
        </EventDetailsItem>
      </div>
      <div class="paginate">
        <BasePagination
          :page="currentPage"
          :total-pages="totalPages"
          :is-loading="isLoading"
          @on-page-change="handlePageChange"
        />
      </div>
    </template>
    <template v-else>
      <EventListLoader
        v-if="isLoading"
        class="event-list-loader"
        button-width="105"
        :hide-button="isMobile"
        :hide-badge="!isMobile"
      >
        <template #button>
          <ButtonSkeletonLoader
            width="105"
            height="38"
          />
        </template>
      </EventListLoader>
      <NoDataFound v-else>
        <template #content>
          <h1 class="text-md text-semibold">
            No events found
          </h1>
          <p class="text-sm text-regular">
            Your events will be listed here once you create an event.
          </p>
        </template>
        <template #action>
          <RouterLink
            class="w-full"
            :to="{
              name: ROUTE.EVENT_CREATE.NAME,
              query: {
                groupId,
              }
            }"
          >
            <BaseButton>Create event</BaseButton>
          </RouterLink>
        </template>
      </NoDataFound>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.hosting-list {
  margin-top: spacings-get(4);

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .status {
    width: fit-content;
  }
}

.event-list-loader {
  :deep(.loader-event-item) {
    grid-template-columns: rem(160) auto rem(106);

    @include media-query-max(mobile) {
      grid-template-columns: 1fr;
    }
  }

  :deep(.button-loader) {
    margin: auto 0;
  }
}
</style>